import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { UserView } from '@olmt/shared'

/** W13 — what the member shares with the others (docs/CONTRACT.md §4, *Privacy toggles*). */
export type Privacy = { shareLastSeen: boolean; shareReadReceipts: boolean }

/**
 * The api's own defaults (docs/CONTRACT.md §4): both toggles are on for every account, which is what
 * the family already had. They stand in until the first `/api/me` answer lands — `bootstrapSession`
 * reads it on every boot — and each read and each accepted patch replaces them.
 */
const PRIVACY_DEFAULTS: Privacy = { shareLastSeen: true, shareReadReceipts: true }

interface AuthState {
  /** Device token — the only credential (docs/CONTRACT.md §2). */
  token: string | null
  /** `GET /api/me`'s user, including `status` (the §11 gate) and `isAdmin` (§6 affordances). */
  user: UserView | null
  /**
   * W13: the two privacy toggles, as `/api/me` reports them (`MeResponse.privacy`) — it is the only
   * route that carries them, because the pair is the member's own policy. Seeded by the boot read
   * and by every later one, and replaced by each accepted `PATCH /api/me/privacy`; a value another
   * device changed arrives with the next read rather than being guessed at here.
   */
  privacy: Privacy
  setSession: (token: string, user: UserView) => void
  setUser: (user: UserView) => void
  setPrivacy: (privacy: Privacy) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      privacy: PRIVACY_DEFAULTS,
      setSession: (token, user) => set({ token, user }),
      setUser: (user) => set({ user }),
      setPrivacy: (privacy) => set({ privacy }),
      // The sign-out door drops the toggles with the session: they belong to the account that
      // was signed in, and the next one must not inherit them.
      clearSession: () => set({ token: null, user: null, privacy: PRIVACY_DEFAULTS }),
    }),
    {
      name: 'olmt-call.session',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // `privacy` rides with the session: a reload must not flip a switch back to a default the
      // member turned off here.
      partialize: (state) => ({ token: state.token, user: state.user, privacy: state.privacy }),
    },
  ),
)
